// src/context/persona/assist/usePersonaJob.js — the workspace's ONE persona-assist job (decisions 6, 10, 11).
// On mount: GET /jobs → an ACTIVE job is tracked (polled every POLL_MS); the newest COMPLETED job is offered for
// review only if its proposal is still unsaved (create: id absent from `rows`; personalize: one GET of the
// persona, etag === base_etag). `start(body)`: POST /jobs; 409 PERSONA_JOB_ACTIVE → fetch and track the running
// job instead (never an error). A job observed moving to `completed` refreshes the credits meter.
// ⚠️ v197 fix: a COMPLETED CREATE job is judged against the persona list only once that list is LOADED
// (`rowsReady`) and re-judged whenever it changes — GET /jobs usually answers before the list, and judging against
// the still-empty list offered an already-saved create again (saving it → 412 → a new id → a duplicate persona).
// ⛔ Nothing runs without `enabled` AND a tenant (the superadmin mount never enables it; the API wrappers throw
// synchronously on a missing tenant, so they must never be reached without one).
import { useCallback, useEffect, useRef, useState } from 'react';
import { getLatestPersonaJob, getPersonaJob, startPersonaJob } from '../../personaAssistApi.js';
import { getPersonaSections } from '../../contextApi.js';
import { refreshEntitlements } from '../../../hooks/useEntitlements.js';
import { POLL_MS, isActive, isUnsavedProposal, jobView } from './assistModel.js';

export default function usePersonaJob({ tenantId, enabled, rows, rowsReady = false }) {
  const [job, setJob] = useState(null);
  const [review, setReview] = useState(false);    // the newest completed job is offered for review
  const [pendingCreate, setPendingCreate] = useState(null); // a completed CREATE job awaiting the loaded list
  const [startError, setStartError] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);
  const alive = useRef(true);
  const wasActive = useRef(false);
  const liveTenant = useRef(null);                // the tenant the mounted effect belongs to
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const stop = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  }, []);

  const track = useCallback((next) => {
    // An answer for a previous workspace (or after unmount) is dropped.
    if (!alive.current || liveTenant.current !== tenantId) return;
    setJob(next);
    if (isActive(next)) {
      wasActive.current = true;
      stop();
      timer.current = setTimeout(async () => {
        timer.current = null;
        try {
          const fresh = await getPersonaJob({ tenant: tenantId, jobId: next.job_id });
          // The create body's name is known only locally — carry it across polls for the running line.
          track(next.label && fresh ? { ...fresh, label: next.label } : fresh);
        } catch (e) {
          // The job vanished (expired / replaced): fall back to whatever GET /jobs answers now.
          if (e?.code === 'PERSONA_JOB_NOT_FOUND') {
            try { track(await getLatestPersonaJob({ tenant: tenantId })); } catch { track(next); }
          } else {
            track(next);                    // transient: try again on the next tick
          }
        }
      }, POLL_MS);
    } else if (wasActive.current) {
      wasActive.current = false;
      if (jobView(next) === 'completed') { refreshEntitlements(); setReview(true); }
    }
  }, [tenantId, stop]);

  // Entering the page: resume an active job, or offer the newest completed one if still unsaved.
  useEffect(() => {
    alive.current = true;
    wasActive.current = false;
    liveTenant.current = enabled ? tenantId : null;
    setJob(null); setReview(false); setStartError(null); setPendingCreate(null);
    if (!enabled || !tenantId) return () => { alive.current = false; stop(); };
    (async () => {
      try {
        const latest = await getLatestPersonaJob({ tenant: tenantId });
        if (!alive.current || liveTenant.current !== tenantId || !latest) return;
        if (isActive(latest)) { track(latest); return; }
        if (jobView(latest) !== 'completed') { setJob(latest); return; }
        if (latest.kind === 'create') {
          // Judged at render time, against the LOADED list (see the header note).
          setJob(latest);
          setPendingCreate(latest);
          return;
        }
        let etag;
        if (latest.kind === 'personalize' && latest.persona_id) {
          try {
            etag = (await getPersonaSections({ tenant: tenantId, id: latest.persona_id })).etag;
          } catch { etag = undefined; }
        }
        if (!alive.current || liveTenant.current !== tenantId) return;
        setJob(latest);
        setReview(isUnsavedProposal(latest, rowsRef.current, etag));
      } catch { /* no job information — the aids still work */ }
    })();
    return () => { alive.current = false; stop(); };
  }, [enabled, tenantId, track, stop]);

  const start = useCallback(async (body) => {
    if (!enabled || !tenantId) return false;
    setStarting(true); setStartError(null); setReview(false); setPendingCreate(null);
    try {
      const res = await startPersonaJob({ tenant: tenantId, body });
      track({
        job_id: res.job_id, kind: res.kind, status: res.status, stale: false, proposal: null,
        persona_id: body?.persona_id || null, suggested_id: null, warnings: [], documents: [],
        label: body?.name || null,
      });
      return true;
    } catch (e) {
      if (e?.code === 'PERSONA_JOB_ACTIVE') {
        try { track(await getLatestPersonaJob({ tenant: tenantId })); } catch { /* keep the error line */ }
      }
      setStartError(e);
      return false;
    } finally {
      if (alive.current) setStarting(false);
    }
  }, [enabled, tenantId, track]);

  // Page-session only: nothing is stored, so a re-entry offers the review again if still unsaved.
  // An ACTIVE job (stale included) is never dropped: it is still tracked.
  const dismiss = useCallback(() => {
    setReview(false);
    setPendingCreate(null);
    setStartError(null);
    setJob((j) => (isActive(j) ? j : null));
  }, []);

  const createReview = Boolean(pendingCreate && rowsReady && isUnsavedProposal(pendingCreate, rows));
  return {
    job, view: jobView(job), busy: isActive(job) || starting, review: review || createReview,
    start, startError, dismiss, starting,
  };
}
